import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaNewspaper,
  FaBlog,
  FaPlus,
  FaEdit,
  FaEye,
  FaSignOutAlt,
  FaUser,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../../config/api";

const Dashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState({
    totalNews: 0,
    totalBlogs: 0,
    publishedNews: 0,
    draftNews: 0,
  });
  const [recentNews, setRecentNews] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get admin from localStorage
    const adminData = localStorage.getItem("adminUser");
    if (adminData) {
      setAdmin(JSON.parse(adminData));
    }

    // Fetch dashboard data
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch news
      const newsResponse = await api.get("/news");
      const newsData = newsResponse.data;

      // Fetch blogs
      const blogResponse = await api.get("/blog");
      const blogData = blogResponse.data;

      // Calculate stats
      setStats({
        totalNews: newsData.length,
        totalBlogs: blogData.length,
        publishedNews: newsData.filter((n) => n.status === "published").length,
        draftNews: newsData.filter((n) => n.status === "draft").length,
      });

      // Get recent items (last 5)
      setRecentNews(newsData.slice(0, 5));
      setRecentBlogs(blogData.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
  };

  // Check user role
  const isSuperAdmin = admin?.role === "super_admin";
  const isAdmin = admin?.role === "admin";
  const isEditor = admin?.role === "editor";

  // Permissions
  const canManageUsers = isSuperAdmin;
  const canEdit = isSuperAdmin || isAdmin;
  const canDelete = isSuperAdmin || isAdmin;
  const canOnlyAdd = isEditor;

  const StatCard = ({ icon: Icon, title, value, color, bgColor }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className={`${bgColor} rounded-xl p-6 shadow-lg`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {title}
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {value}
          </p>
        </div>
        <div className={`${color} p-4 rounded-full`}>
          <Icon className="text-2xl text-white" />
        </div>
      </div>
    </motion.div>
  );

  const QuickActionButton = ({ icon: Icon, label, onClick, color }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`${color} text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all`}
    >
      <Icon />
      <span>{label}</span>
    </motion.button>
  );

  const RecentItem = ({ item, type }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow hover:shadow-lg transition-all border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">
            {item.title}
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
            {item.excerpt || item.content?.substring(0, 100)}
          </p>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span className="flex items-center space-x-1">
              <FaClock />
              <span>{new Date(item.created_at).toLocaleDateString()}</span>
            </span>
            {item.status && (
              <span
                className={`flex items-center space-x-1 ${
                  item.status === "published"
                    ? "text-green-600"
                    : "text-yellow-600"
                }`}
              >
                {item.status === "published" ? (
                  <FaCheckCircle />
                ) : (
                  <FaTimesCircle />
                )}
                <span className="capitalize">{item.status}</span>
              </span>
            )}
          </div>
        </div>

        {/* Show edit/view buttons only for admin and super_admin */}
        {canEdit && (
          <div className="flex space-x-2 ml-4">
            <button
              onClick={() => navigate(`/admin/${type}/edit/${item.id}`)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
              title="Edit"
            >
              <FaEdit />
            </button>
            <button
              className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all"
              title="View"
            >
              <FaEye />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Dashboard
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Welcome back, {admin?.full_name || admin?.username}!
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <FaUser className="text-gray-600 dark:text-gray-400" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {admin?.username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {admin?.role?.replace("_", " ")}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid - Hide for editors */}
        {!canOnlyAdd && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={FaNewspaper}
              title="Total News"
              value={stats.totalNews}
              color="bg-blue-600"
              bgColor="bg-blue-50 dark:bg-blue-900/20"
            />
            <StatCard
              icon={FaBlog}
              title="Total Blogs"
              value={stats.totalBlogs}
              color="bg-purple-600"
              bgColor="bg-purple-50 dark:bg-purple-900/20"
            />
            <StatCard
              icon={FaCheckCircle}
              title="Published"
              value={stats.publishedNews}
              color="bg-green-600"
              bgColor="bg-green-50 dark:bg-green-900/20"
            />
            <StatCard
              icon={FaTimesCircle}
              title="Drafts"
              value={stats.draftNews}
              color="bg-yellow-600"
              bgColor="bg-yellow-50 dark:bg-yellow-900/20"
            />
          </div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg mb-8"
        >
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="flex flex-wrap gap-4">
            {/* Add News - All roles */}
            <QuickActionButton
              icon={FaPlus}
              label="Add News"
              onClick={() => navigate("/admin/news/create")}
              color="bg-blue-600 hover:bg-blue-700"
            />

            {/* Add Blog - All roles */}
            <QuickActionButton
              icon={FaPlus}
              label="Add Blog"
              onClick={() => navigate("/admin/blog/create")}
              color="bg-purple-600 hover:bg-purple-700"
            />

            {/* Manage News - Admin and Super Admin only */}
            {canEdit && (
              <QuickActionButton
                icon={FaNewspaper}
                label="Manage News"
                onClick={() => navigate("/admin/news")}
                color="bg-green-600 hover:bg-green-700"
              />
            )}

            {/* Manage Blogs - Admin and Super Admin only */}
            {canEdit && (
              <QuickActionButton
                icon={FaBlog}
                label="Manage Blogs"
                onClick={() => navigate("/admin/blog")}
                color="bg-orange-600 hover:bg-orange-700"
              />
            )}

            {/* Manage Users - Super Admin only */}
            {canManageUsers && (
              <QuickActionButton
                icon={FaUsers}
                label="Manage Users"
                onClick={() => navigate("/admin/users")}
                color="bg-purple-600 hover:bg-purple-700"
              />
            )}
          </div>
        </motion.div>

        {/* Recent Content Grid - Hide for editors */}
        {!canOnlyAdd && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent News */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                  <FaNewspaper className="text-blue-600" />
                  <span>Recent News</span>
                </h2>
                <button
                  onClick={() => navigate("/admin/news")}
                  className="text-blue-600 hover:text-blue-700 text-sm font-semibold"
                >
                  View All →
                </button>
              </div>

              {recentNews.length > 0 ? (
                <div className="space-y-3">
                  {recentNews.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all"
                    >
                      {item.image && (
                        <img
                          src={`https://worganaturals.com${item.image}`}
                          alt={item.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {item.excerpt || item.content.substring(0, 100)}...
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center space-x-1">
                            <FaEye />
                            <span>{item.views || 0}</span>
                          </span>
                          <span>
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  No news articles yet
                </p>
              )}
            </div>

            {/* Recent Blogs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                  <FaBlog className="text-purple-600" />
                  <span>Recent Blogs</span>
                </h2>
                <button
                  onClick={() => navigate("/admin/blog")}
                  className="text-purple-600 hover:text-purple-700 text-sm font-semibold"
                >
                  View All →
                </button>
              </div>

              {recentBlogs.length > 0 ? (
                <div className="space-y-3">
                  {recentBlogs.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all"
                    >
                      {item.image && (
                        <img
                          src={`https://worganaturals.com${item.image}`}
                          alt={item.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {item.excerpt}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center space-x-1">
                            <FaEye />
                            <span>{item.views || 0}</span>
                          </span>
                          <span>
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  No blog posts yet
                </p>
              )}
            </div>
          </div>
        )}

        {/* Editor-only view */}
        {canOnlyAdd && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center"
          >
            <FaPlus className="text-6xl text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Ready to Create Content?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Use the quick actions above to add new news articles or blog
              posts.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate("/admin/news/create")}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all"
              >
                Create News Article
              </button>
              <button
                onClick={() => navigate("/admin/blog/create")}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-all"
              >
                Create Blog Post
              </button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
